import React, { useState, useCallback } from 'react';
import posthog from 'posthog-js';
import { submitFeedback } from '../../api/feedback';
import type { FeedbackPayload } from '../../api/feedback';

type FeedbackType = 'review' | 'feedback' | 'support';

const TYPE_LABELS: Record<FeedbackType, string> = {
  review: '⭐ Review',
  feedback: '💡 Feedback',
  support: '🆘 Support',
};

const StarRating: React.FC<{ value: number; onChange: (v: number) => void }> = ({
  value,
  onChange,
}) => (
  <div className="flex gap-1" role="group" aria-label="Star rating">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        aria-label={`${n} star${n > 1 ? 's' : ''}`}
        onClick={() => onChange(n)}
        className={[
          'text-2xl leading-none transition-transform hover:scale-125',
          n <= value ? 'text-amber-400' : 'text-gray-600',
        ].join(' ')}
      >
        ★
      </button>
    ))}
  </div>
);

const FeedbackWidget: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<FeedbackType>('feedback');
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = useCallback(() => {
    setOpen(false);
    // Reset after animation completes
    setTimeout(() => {
      setSent(false);
      setMessage('');
      setRating(0);
      setType('feedback');
      setError(null);
    }, 300);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!message.trim()) {
        setError('Please enter a message.');
        return;
      }
      setSending(true);
      setError(null);
      try {
        const payload: FeedbackPayload = {
          type,
          message: message.trim(),
          page_context: window.location.pathname,
        };
        if (type === 'review' && rating > 0) payload.rating = rating;
        await submitFeedback(payload);
        posthog.capture('user_feedback', {
          feedback_type: type,
          rating: type === 'review' && rating > 0 ? rating : undefined,
          message: message.trim(),
          page: window.location.pathname,
        });
        setSent(true);
      } catch {
        setError('Failed to send — please try again.');
      } finally {
        setSending(false);
      }
    },
    [type, rating, message]
  );

  return (
    <>
      {/* Floating trigger button */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Leave feedback"
        title="Leave a review or get support"
        className="fixed bottom-20 right-6 z-50 flex items-center gap-2 px-4 py-2.5 rounded-full
                   bg-blue-600 hover:bg-blue-500 active:scale-95 text-white text-sm font-medium
                   shadow-lg shadow-blue-900/40 transition-all duration-150
                   md:bottom-22 md:right-8"
        style={{ display: open ? 'none' : undefined }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
        </svg>
        Feedback
      </button>

      {/* Modal overlay */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-end p-4 md:p-8"
          role="dialog"
          aria-modal="true"
          aria-label="Feedback form"
        >
          {/* Backdrop (click to close) */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={close}
            aria-hidden="true"
          />

          {/* Panel */}
          <div className="relative w-full max-w-sm bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl p-6 animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white font-semibold text-base">Share your thoughts</h2>
              <button
                type="button"
                onClick={close}
                aria-label="Close feedback form"
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {sent ? (
              <div className="text-center py-8">
                <div className="text-4xl mb-3">🎉</div>
                <p className="text-white font-medium mb-1">Thank you!</p>
                <p className="text-gray-400 text-sm">Your feedback helps us improve Pinecurl.</p>
                <button
                  type="button"
                  onClick={close}
                  className="mt-6 btn btn--primary w-full"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                {/* Type selector */}
                <div className="flex gap-2 mb-4">
                  {(Object.keys(TYPE_LABELS) as FeedbackType[]).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setType(t)}
                      className={[
                        'flex-1 py-1.5 text-xs rounded-lg font-medium transition-colors',
                        type === t
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-800 text-gray-400 hover:text-white',
                      ].join(' ')}
                    >
                      {TYPE_LABELS[t]}
                    </button>
                  ))}
                </div>

                {/* Star rating (only for review) */}
                {type === 'review' && (
                  <div className="mb-4">
                    <p className="text-gray-400 text-xs mb-2">How would you rate your experience?</p>
                    <StarRating value={rating} onChange={setRating} />
                  </div>
                )}

                {/* Message */}
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={
                    type === 'review'
                      ? 'Tell us what you think about Pinecurl…'
                      : type === 'support'
                      ? 'Describe your issue and we\'ll get back to you…'
                      : 'Share an idea or suggestion…'
                  }
                  rows={4}
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5
                             text-white text-sm placeholder-gray-500 resize-none
                             focus:outline-none focus:border-blue-500 transition-colors"
                  required
                />

                {error && (
                  <p className="text-red-400 text-xs mt-1">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={sending || !message.trim()}
                  className="btn btn--primary w-full mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? 'Sending…' : 'Send'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackWidget;
